import { Context, useContext, useReducer, useEffect } from 'react';

import { AppContext, AppContextState, INITIAL_STATE } from './AppContext.ts';
import { AppReducer } from './AppReducer.ts';
import { useWidth } from '@/hooks/useWidth.ts';
import { isWidthDown } from '@mui/material/Hidden/withWidth';
import { AuthTempInfo, AuthUser } from '@/types/Auth.ts';
import { Company } from '@/types/Company.ts';
import { CompletedSupport } from '@/types/Support';
import { Channel } from '@/types/Channel.ts';

interface AppProviderProps {
  children: React.ReactNode | Array<React.ReactNode>;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const [{ auth, menu, layout, loaded, company, channels, support }, dispatch] = useReducer(
    AppReducer,
    INITIAL_STATE as AppContextState,
  );

  const width = useWidth();

  useEffect(() => {
    dispatch({ type: 'setBreakPoint', payload: width });

    if (isWidthDown('sm', width)) {
      dispatch({ type: 'setDevice', payload: 'mobile' });
    } else if (isWidthDown('md', width)) {
      dispatch({ type: 'setDevice', payload: 'tablet' });
    } else {
      dispatch({ type: 'setDevice', payload: 'pc' });
    }
  }, [width, layout.breakpoint, layout.device, dispatch, isWidthDown]);

  const setAuthUser = (value: AuthUser) => {
    dispatch({ type: 'setAuthUser', payload: value });
  };

  const setAuthTempInfo = (value: AuthTempInfo) => {
    dispatch({ type: 'setAuthTempInfo', payload: value });
  };

  const setLoaded = (value: boolean) => {
    dispatch({ type: 'setLoaded', payload: value });
  };

  const setMenuOpen = (value: boolean) => {
    dispatch({ type: 'setMenuOpen', payload: value });
  };

  const setCompany = (value: Company) => {
    dispatch({ type: 'setCompany', payload: value });
  };

  const setChannels = (value: Channel[]) => {
    dispatch({ type: 'setChannels', payload: value });
  };

  const setCompletedSupport = (value: CompletedSupport[]) => {
    dispatch({ type: 'setCompletedSupport', payload: value });
  };

  const cleanState = () => {
    dispatch({ type: 'cleanState' });
  };

  return (
    <AppContext.Provider
      value={
        {
          auth,
          layout,
          menu,
          company,
          channels,
          support,
          loaded,
          setLoaded,
          setMenuOpen,
          setAuthUser,
          setAuthTempInfo,
          setCompany,
          setChannels,
          setCompletedSupport,
          cleanState,
        } as AppContextState
      }
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextState =>
  useContext(AppContext as unknown as Context<AppContextState>);
